/**
 * Compliance Reports - Keystone RFP Agent
 * Enhanced wrapper around core ComplianceReports with RFP-specific filtering and quick actions
 */

import React, { useState, useMemo, useEffect } from 'react';
import { ComplianceReports as CoreComplianceReports } from '@tracekit/core';
import { Card, Button, Input, Select, Badge, Modal } from '@tracekit/core';
import { RFP_REPORT_TEMPLATES } from './ReportTemplates';
import { Filter, X, Copy, Archive, Download, Share2, Trash2, MoreVertical, Scissors, UserPlus, Users, AlertCircle } from 'lucide-react';
import { listReports, downloadReportPDF, downloadReportV2, getReport } from '@tracekit/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspace, useToastStore } from '@tracekit/core';
import { Report } from '@tracekit/core';
import { WordCountOptimizer } from './WordCountOptimizer';
import { WordCountDisplay, calculateReportWordCount, calculateSectionWordCounts } from './WordCountDisplay';
import { validateWordCount, getReportStaffAssignments } from '../services/rfpApi';
import { StaffAssignmentModal } from './StaffAssignmentModal';

/**
 * Proposal Word Count Banner Component
 * Displays word count and optimization button when viewing a proposal
 */
function ProposalWordCountBanner({ reportId }: { reportId: string }) {
  const { projectId } = useWorkspace();
  const { data: report } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    enabled: !!reportId,
  });
  
  const { data: validation } = useQuery({
    queryKey: ['word-count-validation', reportId],
    queryFn: () => validateWordCount(reportId),
    enabled: !!reportId && !!report,
  });
  
  const [showOptimizer, setShowOptimizer] = useState(false);
  const queryClient = useQueryClient();
  
  if (!report) return null;
  
  const wordCount = calculateReportWordCount(report.sections || []);
  const wordLimit = (report.metadata as any)?.word_limit;
  const sectionWordCounts = validation?.section_word_counts;
  const sectionLimits = validation?.section_limits;
  
  return (
    <>
      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <WordCountDisplay
              wordCount={wordCount}
              wordLimit={wordLimit}
              sectionWordCounts={sectionWordCounts}
              sectionLimits={sectionLimits}
              showSectionBreakdown={true}
              size="md"
            />
          </div>
          <div className="ml-4">
            <Button
              variant="default"
              onClick={() => setShowOptimizer(true)}
            >
              <Scissors className="h-4 w-4 mr-2" />
              Optimize Word Count
            </Button>
          </div>
        </div>
        
        {/* Validation Warnings */}
        {validation && !validation.is_compliant && (
          <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-800">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900 dark:text-red-100 mb-1">
                    Word Count Limit Exceeded
                  </h4>
                  {validation.violations.total.length > 0 && (
                    <ul className="text-sm text-red-800 dark:text-red-200 list-disc list-inside space-y-1">
                      {validation.violations.total.map((v, i) => (
                        <li key={i}>{v.message}</li>
                      ))}
                    </ul>
                  )}
                  {validation.violations.sections.length > 0 && (
                    <ul className="text-sm text-red-800 dark:text-red-200 list-disc list-inside space-y-1 mt-2">
                      {validation.violations.sections.map((v, i) => (
                        <li key={i}>{v.message}</li>
                      ))}
                    </ul>
                  )}
                  <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                    Please optimize the word count before submission.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
      
      {/* Word Count Optimizer Modal */}
      {showOptimizer && report && (
        <Modal
          isOpen={true}
          onClose={() => setShowOptimizer(false)}
          title="Word Count Optimization"
          size="xl"
        >
          <div className="max-h-[80vh] overflow-y-auto">
            <WordCountOptimizer
              report={report}
              currentWordCount={wordCount}
              targetWordCount={wordLimit}
              onOptimizationComplete={() => {
                if (projectId) {
                  queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
                }
                queryClient.invalidateQueries({ queryKey: ['report', reportId] });
                queryClient.invalidateQueries({ queryKey: ['word-count-validation', reportId] });
                setShowOptimizer(false);
              }}
              onClose={() => setShowOptimizer(false)}
            />
          </div>
        </Modal>
      )}
    </>
  );
}

interface FilterState {
  dateFrom: string;
  dateTo: string;
  client: string;
  projectType: string;
  status: string;
  serviceLine: string;
}

interface ComplianceReportsProps {
  initialReportId?: string; // Optional report ID to open in viewer on mount
}

export function ComplianceReports({ initialReportId }: ComplianceReportsProps = {}) {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: '',
    dateTo: '',
    client: '',
    projectType: '',
    status: 'all',
    serviceLine: 'all',
  });
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [showWordCountOptimizer, setShowWordCountOptimizer] = useState(false);
  const [optimizerReport, setOptimizerReport] = useState<Report | null>(null);
  const [showAssignStaffModal, setShowAssignStaffModal] = useState(false);
  const [assignStaffReportId, setAssignStaffReportId] = useState<string | null>(null);

  // Fetch reports with RFP-specific filtering (exclude workplan generations on server side)
  const { data: reports } = useQuery({
    queryKey: ['reports', projectId, 'rfp_proposals'],
    queryFn: async () => {
      const result = await listReports(projectId!, { exclude_report_type: 'workplan_generation' });
      // Debug: Log any workplans that slip through (should be none)
      const workplans = result.filter(r => r.report_type === 'workplan_generation');
      if (workplans.length > 0) {
        console.error('[ComplianceReports] API returned workplans despite exclude_report_type filter:', workplans);
      }
      return result;
    },
    enabled: !!projectId,
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache old data
  });

  // Filter reports based on RFP-specific criteria
  // NOTE: Server-side filtering already excludes workplans, this is just a safety check
  const filteredReports = useMemo(() => {
    if (!reports) return [];
    
    console.log('[ComplianceReports] Filtering reports. Total from API:', reports.length);
    const workplansInData = reports.filter(r => r.report_type === 'workplan_generation');
    if (workplansInData.length > 0) {
      console.error('[ComplianceReports] CRITICAL: Workplans found in API response:', workplansInData);
    }
    
    return reports.filter((report: Report) => {
      // STRICT EXCLUSION: Never show workplan_generation reports
      if (report.report_type === 'workplan_generation') {
        console.warn('[ComplianceReports] Workplan detected and excluded:', report.id, report.name);
        return false;
      }
      
      // Include RFP proposals (direct type)
      if (report.report_type === 'rfp_proposal') {
        // Continue to apply additional filters below
      } else if (report.report_type === 'custom') {
        // For custom reports, check metadata if available (may not be in API response)
        const metadata = (report as any).metadata || (report as any).meta || {};
        // Only include if explicitly marked as RFP proposal or has rfp_artifact_id
        const isRfpProposal = 
          metadata.report_type_override === 'rfp_proposal' || 
          metadata.rfp_artifact_id;
        
        // If metadata is empty (not in API response), exclude to be safe
        // Server-side filtering should have already handled this
        if (!isRfpProposal) {
          return false;
        }
      } else {
        // Exclude any other report types
        return false;
      }

      // Apply additional filters - use name with fallback to client_name
      const metadata = (report as any).metadata || (report as any).meta || {};
      const reportName = report.name || report.client_name;
      if (filters.client && !reportName?.toLowerCase().includes(filters.client.toLowerCase())) {
        return false;
      }
      if (filters.status !== 'all' && report.status !== filters.status) {
        return false;
      }
      if (filters.dateFrom && new Date(report.created_at) < new Date(filters.dateFrom)) {
        return false;
      }
      if (filters.dateTo && new Date(report.created_at) > new Date(filters.dateTo)) {
        return false;
      }
      if (filters.projectType && !metadata.rfp_title?.toLowerCase().includes(filters.projectType.toLowerCase())) {
        return false;
      }
      if (filters.serviceLine && !metadata.service_line?.toLowerCase().includes(filters.serviceLine.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  }, [reports, filters]);

  const updateStatusMutation = useMutation({
    mutationFn: async ({ reportId, status }: { reportId: string; status: string }) => {
      return updateReportStatusWithValidation(reportId, status as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
      setActionMenuOpen(null);
      addToast({
        type: 'success',
        title: 'Status updated',
        message: 'Proposal status has been updated successfully',
      });
    },
    onError: (error: any) => {
      addToast({
        type: 'error',
        title: 'Failed to update status',
        message: error.message || 'Could not update proposal status',
      });
    },
  });

  const handleClone = async (reportId: string) => {
    try {
      // Clone by creating a new report with same questions and metadata
      const report = reports?.find((r: Report) => r.id === reportId);
      if (!report) {
        addToast({
          type: 'error',
          title: 'Report not found',
          message: 'Cannot clone - report not found',
        });
        return;
      }

      // Get report sections to extract questions
      const { getReport } = await import('@tracekit/core');
      const fullReport = await getReport(reportId);
      
      // Create new report with same questions
      const { submitReportJob } = await import('@tracekit/core');
      const questions = (fullReport.sections || []).map((s: any) => ({
        section: s.section_name,
        question: s.question,
      }));

      if (questions.length === 0) {
        addToast({
          type: 'error',
          title: 'Cannot clone',
          message: 'Report has no sections to clone',
        });
        return;
      }

      const metadata = (report as any).metadata || {};
      await submitReportJob({
        project_id: report.project_id,
        report_type: 'custom',
        client_name: report.client_name, // Keep client_name as is
        name: `${report.name || report.client_name} (Copy)`, // Use name for cloned report
        questions,
        metadata: {
          ...metadata,
          cloned_from: reportId,
          report_type_override: 'rfp_proposal',
        },
        enable_cache: true,
      });

      addToast({
        type: 'success',
        title: 'Proposal cloned',
        message: 'A new proposal has been created with the same content',
      });
      setActionMenuOpen(null);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Clone failed',
        message: error.message || 'Failed to clone proposal',
      });
    }
  };

  const handleArchive = async (reportId: string) => {
    if (confirm('Archive this proposal?')) {
      await updateStatusMutation.mutateAsync({ reportId, status: 'archived' });
    }
  };

  const handleExport = async (reportId: string, format: 'pdf' | 'markdown') => {
    try {
      if (format === 'pdf') {
        const blob = await downloadReportPDF(reportId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `proposal-${reportId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const content = await downloadReportV2(reportId);
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `proposal-${reportId}.md`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      setActionMenuOpen(null);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed: ' + (error as Error).message);
    }
  };

  const handleDelete = async (reportId: string) => {
    if (confirm('Archive this proposal? It will be moved to archived status.')) {
      try {
        await updateStatusMutation.mutateAsync({ reportId, status: 'archived' });
        addToast({
          type: 'success',
          title: 'Proposal archived',
          message: 'The proposal has been moved to archived status',
        });
      } catch (error: any) {
        addToast({
          type: 'error',
          title: 'Archive failed',
          message: error.message || 'Failed to archive proposal',
        });
      }
    }
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      client: '',
      projectType: '',
      status: 'all',
      serviceLine: 'all',
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all').length;

  // Check if we're viewing a specific proposal (hide filters)
  // Use useEffect to track URL changes and initialReportId
  const [isViewingProposal, setIsViewingProposal] = useState(false);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const proposalId = urlParams.get('proposal_id');
    setIsViewingProposal(!!proposalId || !!initialReportId);
    
    // Also listen for URL changes
    const handleLocationChange = () => {
      const newParams = new URLSearchParams(window.location.search);
      const newProposalId = newParams.get('proposal_id');
      setIsViewingProposal(!!newProposalId || !!initialReportId);
    };
    
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [initialReportId]);

  return (
    <div className="space-y-6">
      {/* RFP-Specific Filter Panel - Hide when viewing a proposal */}
      {!isViewingProposal && (
      <Card>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">RFP Proposal Filters</h3>
            <div className="flex gap-2">
              {activeFilterCount > 0 && (
                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Clear ({activeFilterCount})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium mb-1">Date From</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date To</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, dateTo: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Client Name</label>
                <Input
                  value={filters.client}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, client: e.target.value })}
                  placeholder="Filter by client"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Project Type</label>
                <Input
                  value={filters.projectType}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, projectType: e.target.value })}
                  placeholder="Filter by project type"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <Select
                  value={filters.status}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="all">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="in_review">In Review</option>
                  <option value="approved">Approved</option>
                  <option value="submitted">Submitted</option>
                  <option value="archived">Archived</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Service Line</label>
                <Select
                  value={filters.serviceLine}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, serviceLine: e.target.value })}
                >
                  <option value="all">All Service Lines</option>
                  <option value="contaminated-sites">Contaminated Sites</option>
                  <option value="environmental-engineering">Environmental Engineering</option>
                  <option value="biology-eia">Biology/EIA</option>
                  <option value="iaq-hazmat">IAQ/HazMat</option>
                </Select>
              </div>
            </div>
          )}

          {/* Active Filter Chips */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {filters.dateFrom && (
                <Badge variant="default" className="flex items-center gap-1">
                  From: {new Date(filters.dateFrom).toLocaleDateString()}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setFilters({ ...filters, dateFrom: '' })}
                  />
                </Badge>
              )}
              {filters.dateTo && (
                <Badge variant="default" className="flex items-center gap-1">
                  To: {new Date(filters.dateTo).toLocaleDateString()}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setFilters({ ...filters, dateTo: '' })}
                  />
                </Badge>
              )}
              {filters.client && (
                <Badge variant="default" className="flex items-center gap-1">
                  Client: {filters.client}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setFilters({ ...filters, client: '' })}
                  />
                </Badge>
              )}
              {filters.projectType && (
                <Badge variant="default" className="flex items-center gap-1">
                  Type: {filters.projectType}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setFilters({ ...filters, projectType: '' })}
                  />
                </Badge>
              )}
              {filters.status !== 'all' && (
                <Badge variant="default" className="flex items-center gap-1">
                  Status: {filters.status}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setFilters({ ...filters, status: 'all' })}
                  />
                </Badge>
              )}
              {filters.serviceLine !== 'all' && (
                <Badge variant="default" className="flex items-center gap-1">
                  Service: {filters.serviceLine}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setFilters({ ...filters, serviceLine: 'all' })}
                  />
                </Badge>
              )}
            </div>
          )}
        </div>
      </Card>
      )}

      {/* Word Count Display Banner - Show when viewing a proposal */}
      {(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const proposalId = urlParams.get('proposal_id') || initialReportId;
        
        if (!proposalId) return null;
        
        return (
          <ProposalWordCountBanner reportId={proposalId} />
        );
      })()}

      {/* Core ComplianceReports Component */}
      {/* Hide filters and header when viewing a specific proposal */}
      {(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const proposalId = urlParams.get('proposal_id');
        const isViewingProposal = !!proposalId || !!initialReportId;
        
        return (
          <CoreComplianceReports
            fallbackTemplates={RFP_REPORT_TEMPLATES}
            title="RFP Proposals"
            description="Generate and manage RFP proposals"
            onReportTypeMapper={() => 'custom'} // RFP proposals use 'custom' type with metadata override
            initialReportId={initialReportId || proposalId || undefined} // Pass initial report ID to open in viewer
            hideHeader={isViewingProposal} // Hide header when viewing proposal
            reportsQueryKeySuffix="rfp_proposals"
            listReportsOptions={{ exclude_report_type: 'workplan_generation' }}
          />
        );
      })()}

      {/* Quick Actions Modal */}
      {selectedReportId && actionMenuOpen === selectedReportId && (
        <Modal
          isOpen={true}
          onClose={() => {
            setActionMenuOpen(null);
            setSelectedReportId(null);
          }}
          title="Proposal Actions"
        >
          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleClone(selectedReportId)}
            >
              <Copy className="h-4 w-4 mr-2" />
              Clone Proposal
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleArchive(selectedReportId)}
            >
              <Archive className="h-4 w-4 mr-2" />
              Archive Proposal
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleExport(selectedReportId, 'pdf')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleExport(selectedReportId, 'markdown')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export Markdown
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => {
                alert('Share functionality will use core ReportSharing component');
                setActionMenuOpen(null);
              }}
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share Proposal
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={async () => {
                try {
                  const { getReport } = await import('@tracekit/core');
                  const report = await getReport(selectedReportId);
                  setOptimizerReport(report);
                  setShowWordCountOptimizer(true);
                  setActionMenuOpen(null);
                } catch (error: any) {
                  addToast({
                    type: 'error',
                    title: 'Failed to load proposal',
                    message: error.message || 'Could not load proposal for optimization',
                  });
                }
              }}
            >
              <Scissors className="h-4 w-4 mr-2" />
              Optimize Word Count
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => {
                setAssignStaffReportId(selectedReportId);
                setShowAssignStaffModal(true);
                setActionMenuOpen(null);
              }}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Assign Staff
            </Button>
            <div className="border-t pt-2 mt-2">
              <Button
                variant="outline"
                className="w-full justify-start text-red-600 hover:text-red-700"
                onClick={() => handleDelete(selectedReportId)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Proposal
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Word Count Optimizer Modal */}
      {showWordCountOptimizer && optimizerReport && (
        <Modal
          isOpen={true}
          onClose={() => {
            setShowWordCountOptimizer(false);
            setOptimizerReport(null);
          }}
          title="Word Count Optimization"
          size="xl"
        >
          <div className="max-h-[80vh] overflow-y-auto">
            <WordCountOptimizer
              report={optimizerReport}
              currentWordCount={calculateReportWordCount(optimizerReport.sections || [])}
              targetWordCount={(optimizerReport.meta as any)?.word_limit}
              onOptimizationComplete={() => {
                queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
                queryClient.invalidateQueries({ queryKey: ['report', optimizerReport.id] });
                setShowWordCountOptimizer(false);
                setOptimizerReport(null);
                addToast({
                  type: 'success',
                  title: 'Optimization Applied',
                  message: 'Word count optimization has been applied to the proposal',
                });
              }}
              onClose={() => {
                setShowWordCountOptimizer(false);
                setOptimizerReport(null);
              }}
            />
          </div>
        </Modal>
      )}

      {/* Assign Staff Modal */}
      {showAssignStaffModal && assignStaffReportId && (
        <StaffAssignmentModal
          reportId={assignStaffReportId}
          reportName={reports?.find((r: Report) => r.id === assignStaffReportId)?.name}
          projectId={projectId || ''}
          isOpen={showAssignStaffModal}
          onClose={() => {
            setShowAssignStaffModal(false);
            setAssignStaffReportId(null);
          }}
          onSuccess={() => {
            // Invalidate all assignment queries to refresh counts everywhere
            queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
            queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
            queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
          }}
        />
      )}
    </div>
  );
}
